import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import { Typography } from "../../Components/Typography";
import AcceptMentorshipDialog from "../../Components/AcceptMentorshipDialog";
import RejectMentorshipDialog from "../../Components/RejectMentorshipDialog";
import IdeaAcceptedDialog from "../../Components/IdeaAcceptedDialog";
import UploadFileDialog from "../../Components/UploadFileDialog";
import Avatar from "../../Components/Avatar";
import Tabs from "../../Components/Tabs";
import Table from "../../Components/Table";
import ProgressTimeline from "../../Components/ProgressTimeline";
import { useAuth } from "../../Context/authContext";
import {
  getIdeaById,
  acceptMentorship,
  rejectMentorship,
  updateIdea,
  sendEmail,
  getAllIdeaUsers,
} from "../../Utils/apiServices";
import IdeaClarityDialog from "../../Components/IdeaClarityDialog";
import IdeaAttachmentsSection from "../EngagementDetail/IdeaAttachmentsSection";
import { readIdeaUrlField } from "../EngagementDetail/ideaDetailUiUtils";
import toast from "../../Utils/toastManager";

const MentorIdeaDetail = ({ isReadOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeStage, setActiveStage] = useState(0);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showAcceptedSuccessDialog, setShowAcceptedSuccessDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showClarityDialog, setShowClarityDialog] = useState(false);
  const [savingClarity, setSavingClarity] = useState(false);
  // idea_users rows for this idea — drives the Team Members table.
  const [ideaTeamRows, setIdeaTeamRows] = useState([]);

  useEffect(() => {
    const fetchIdeaTeam = async () => {
      if (!id) return;
      try {
        const res = await getAllIdeaUsers({ idea_id: id });
        const rows = res?.data?.data ?? res?.data ?? [];
        setIdeaTeamRows(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Error loading idea team members:", err);
      }
    };
    fetchIdeaTeam();
  }, [id]);

  // Get student data from navigation state (passed from MentorIdeasReview)
  const navigationState = location.state;
  const studentFromNavigation = navigationState?.primaryStudent || null;

  // Mentor-specific action configuration
  const roleActions = {
    primaryAction: {
      label: "Accept Mentorship",
      icon: "lucide:check",
      className: "border border-[#17B26A] !text-[#079455] bg-white text-sm font-medium hover:bg-slate-50 focus:ring-tertiary-700",
      dialogTitle: "Accept Mentorship",
      dialogDescription: "Write a reason for accepting this idea.",
      successMessage: "Idea Accepted",
    },
    secondaryAction: {
      label: "Reject Mentorship",
      icon: "lucide:x",
      className: "border border-[#F04438] !text-[#D92D20] bg-white text-sm font-medium hover:bg-slate-50 focus:ring-tertiary-700",
      dialogTitle: "Reject Mentorship",
      dialogDescription: "Write a reason for rejecting this idea.",
    },
  };

  const sanitizeStudentResponse = (value) => {
    if (value === null || value === undefined) return "";
    const trimmedValue = String(value).trim();
    if (!trimmedValue) return "";

    // Hide obvious placeholder data like "test", "testtt", etc.
    if (/^test+\W*$/i.test(trimmedValue)) return "";
    if (/^in review test\W*$/i.test(trimmedValue)) return "";

    return trimmedValue;
  };

  // Transform API response to component format for mentor
  const transformIdeaData = (apiData) => {
    if (!apiData) return null;

    // Handle different possible response structures
    const data = apiData.data?.data || apiData.data || apiData;

    // For mentor, the API might return { idea: {...}, primaryStudent: {...}, ... }
    const ideaData = data.idea || data;

    // Get primary student info - prefer navigation state, fallback to API data
    const primaryStudent = studentFromNavigation || data.primaryStudent || data.primary_student;
    const studentName = primaryStudent
      ? `${primaryStudent.first_name || ""} ${primaryStudent.last_name || ""}`.trim()
      : "Unknown";
    const studentAvatar = primaryStudent?.profile_picture_url || "";
    const studentEmail = primaryStudent?.email || "";

    // Extract links from API response and convert to file objects
    const linkFiles = [];
    const submissionDate = ideaData.submission_date || ideaData.submissionDate || ideaData.created_at || "";
    const ideaVideoUrl = readIdeaUrlField(ideaData, "idea_video_link", "ideaVideoLink");
    const conceptDiagramUrl = readIdeaUrlField(ideaData, "concept_diagram", "conceptDiagram");
    const pitchVideoUrl = readIdeaUrlField(ideaData, "pitch_video_link", "pitchVideoLink");
    const otherDocumentUrl = readIdeaUrlField(ideaData, "other_document", "otherDocument");
    const ideaImageUrl = readIdeaUrlField(ideaData, "idea_image", "ideaImage");
    
    if (ideaVideoUrl) {
      linkFiles.push({
        id: `idea-video-${ideaData.id}`,
        name: "Idea Video Link",
        size: "Link",
        type: "mp4",
        fileTag: "Video",
        uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) : "-",
        url: ideaVideoUrl,
        isLink: true,
      });
    }

    if (conceptDiagramUrl) {
      linkFiles.push({
        id: `concept-diagram-${ideaData.id}`,
        name: "Concept Diagram Link",
        size: "Link",
        type: "link",
        fileTag: "Diagram",
        uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) : "-",
        url: conceptDiagramUrl,
        isLink: true,
      });
    }
    
    if (pitchVideoUrl) {
      linkFiles.push({
        id: `pitch-video-${ideaData.id}`,
        name: "Pitch Video Link",
        size: "Link",
        type: "mp4",
        fileTag: "Video",
        uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) : "-",
        url: pitchVideoUrl,
        isLink: true,
      });
    }
    
    if (otherDocumentUrl) {
      const urlLower = otherDocumentUrl.toLowerCase();
      let fileType = "doc";
      if (urlLower.includes(".pdf")) fileType = "pdf";
      else if (urlLower.includes(".docx")) fileType = "docx";
      else if (urlLower.includes(".doc")) fileType = "doc";
      else if (urlLower.includes(".jpg") || urlLower.includes(".jpeg") || urlLower.includes(".png")) {
        fileType = "jpg";
      }

      linkFiles.push({
        id: `other-document-${ideaData.id}`,
        name: "Other Document Link",
        size: "Link",
        type: fileType,
        fileTag: "Document",
        uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) : "-",
        url: otherDocumentUrl,
        isLink: true,
      });
    }

    if (ideaImageUrl) {
      linkFiles.push({
        id: `idea-image-${ideaData.id}`,
        name: "Idea Image",
        size: "Link",
        type: "jpg",
        fileTag: "Image",
        uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }) : "-",
        url: ideaImageUrl,
        isLink: true,
      });
    }

    // Combine link files with existing files/attachments
    const existingFiles = ideaData.files || ideaData.attachments || [];
    const allFiles = [...linkFiles, ...existingFiles];

    return {
      id: ideaData.id,
      title: ideaData.title || ideaData.name || ideaData.idea_name || "",
      description: ideaData.description || "",
      submissionDate: submissionDate,
      problemStatement: ideaData.problem_statement || ideaData.problemStatement || ideaData.description || "",
      proposedSolution: ideaData.proposed_solution || ideaData.proposedSolution || ideaData.description || "",
      targetAudience: ideaData.target_audience || ideaData.targetAudience || "",
      existingAlternatives: ideaData.existing_alternatives || ideaData.existingAlternatives || "",
      primaryImpact: ideaData.primary_impact || ideaData.primaryImpact || "",
      ideaVideoLink: readIdeaUrlField(ideaData, "idea_video_link", "ideaVideoLink"),
      conceptDiagram: readIdeaUrlField(ideaData, "concept_diagram", "conceptDiagram"),
      otherDocument: readIdeaUrlField(ideaData, "other_document", "otherDocument"),
      ideaImage: readIdeaUrlField(ideaData, "idea_image", "ideaImage"),
      pitchVideoLink: readIdeaUrlField(ideaData, "pitch_video_link", "pitchVideoLink"),
      ideaClarity: ideaData.idea_clarity || ideaData.ideaClarity || "",
      innovationCategory: ideaData.innovation_category || ideaData.innovationCategory || "",
      submitter: {
        name: studentName,
        avatar: studentAvatar,
        email: studentEmail,
      },
      rubricsScores: ideaData.rubrics_scores || ideaData.rubricsScores || [],
      customerDeepDiveNotes: ideaData.customer_deep_dive_notes || ideaData.customerDeepDiveNotes || "",
      numberOfMentors: ideaData.number_of_mentors || ideaData.numberOfMentors || 0,
      overallScore: ideaData.overall_score || ideaData.overallScore || 0,
      maxOverallScore: ideaData.max_overall_score || ideaData.maxOverallScore || 50,
      rejectionMessage: ideaData.rejection_message || ideaData.rejectionMessage || null,
      files: allFiles,
      reviewCriteria: ideaData.review_criteria || ideaData.reviewCriteria || [],
      overallComment: ideaData.overall_comment || ideaData.overallComment || "",
      currentStage: ideaData.current_stage || ideaData.currentStage || 0,
      daysRemaining: ideaData.days_remaining || data.daysRemaining || null,
      stages: ideaData.stages || [],
      preferenceOrder: navigationState?.preferenceOrder || data.preferenceOrder || data.preference_order || null,
      primaryStudent: primaryStudent,
      questionnaireResponses: ideaData.questionnaire_responses || ideaData.questionnaireResponses || data.questionnaire_responses || data.questionnaireResponses || [],
      innovationExcitesYou: sanitizeStudentResponse(
        ideaData.innovation_excites_you ||
          ideaData.innovationExcitesYou ||
          primaryStudent?.innovation_excites_you ||
          primaryStudent?.innovationExcitesYou ||
          ""
      ),
      successDefinition: sanitizeStudentResponse(
        ideaData.success ||
          ideaData.successDefinition ||
          primaryStudent?.success ||
          primaryStudent?.successDefinition ||
          ""
      ),
      achievementSummary: sanitizeStudentResponse(
        ideaData.achievement_summary ||
          ideaData.achievementSummary ||
          ideaData.achievement ||
          primaryStudent?.achievement ||
          primaryStudent?.achievement_summary ||
          ""
      ),
      // Stage 1 (Shape) fields
      idealCustomerProfile: ideaData.ideal_customer_profile || "",
      idealCustomerProfileUrl: ideaData.ideal_customer_profile_url || "",
      valueProposition: ideaData.value_proposition || "",
      valuePropositionUrl: ideaData.value_proposition_url || "",
      productMarketFit: ideaData.product_market_fit || "",
      productMarketFitUrl: ideaData.product_market_fit_url || "",
      targetAddressableMarket: ideaData.target_addressable_market || "",
      targetAddressableMarketUrl: ideaData.target_addressable_market_url || "",
      mvp: ideaData.mvp || "",
      mvpUrl: ideaData.mvp_url || "",
      // Stage 2 (Secure) fields
      marketValidation: ideaData.market_validation || "",
      marketValidationUrl: ideaData.market_validation_url || "",
      financialProjections: ideaData.financial_projections || "",
      financialProjectionsUrl: ideaData.financial_projections_url || "",
      microGrantProposal: ideaData.micro_grant_proposal || "",
      microGrantProposalUrl: ideaData.micro_grant_proposal_url || "",
      enhanceBusinessModel: ideaData.enhance_business_model || "",
      enhanceBusinessModelUrl: ideaData.enhance_business_model_url || "",
      // Stage 3 (Scale) fields
      productReadiness: ideaData.product_readiness || "",
      productReadinessUrl: ideaData.product_readiness_url || "",
      customerProfile: ideaData.customer_profile || "",
      customerProfileUrl: ideaData.customer_profile_url || "",
      gtmStrategy: ideaData.gtm_strategy || "",
      gtmStrategyUrl: ideaData.gtm_strategy_url || "",
      customerFeedback: ideaData.customer_feedback || "",
      customerFeedbackUrl: ideaData.customer_feedback_url || "",
      currentStep: ideaData.current_step ?? 0,
    };
  };

  useEffect(() => {
    const fetchIdeaData = async () => {
      try {
        setLoading(true);

        // Try to fetch from API
        const response = await getIdeaById(id);

        if (response?.success && response.data) {
          const transformedData = transformIdeaData(response);
          if (transformedData) {
            setIdea(transformedData);
            setActiveStage(transformedData.currentStage || 0);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error fetching idea:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIdeaData();
    }
  }, [id]);

  const handlePrimaryAction = () => {
    setShowAcceptDialog(true);
  };

  const handleSecondaryAction = () => {
    if (roleActions.secondaryAction) {
      setShowRejectDialog(true);
    }
  };

  const handleSaveIdeaClarity = async (emailPayload) => {
    setSavingClarity(true);
    try {
      const emailResponse = await sendEmail(emailPayload);
      if (!emailResponse?.success) {
        toast.error(emailResponse?.error || "Failed to send clarity email.");
        return;
      }

      const response = await updateIdea(id, { idea_clarity: emailPayload.body.trim() });
      if (response?.success) {
        setIdea((prev) => (prev ? { ...prev, ideaClarity: emailPayload.body.trim() } : prev));
        setShowClarityDialog(false);
      } else {
        toast.error(response?.error || "Email sent, but failed to save clarity request on the idea.");
      }
    } catch (error) {
      console.error("Error saving idea clarity:", error);
      toast.error(error?.error || "An error occurred while sending the clarity request.");
    } finally {
      setSavingClarity(false);
    }
  };

  const handlePrimaryActionConfirm = async (reason) => {
    try {
      if (!id || !user?.id) {
        console.error("Missing idea ID or user ID");
        return;
      }

      const response = await acceptMentorship(id, user.id, reason);
      
      if (response?.success || response?.status === 200) {
        // Update the idea state with new status
        setIdea((prevIdea) => ({
          ...prevIdea,
          status: "ACTIVE",
          decision_reason: reason,
          decided_by: user.id,
        }));
        
        // Show success dialog
        setShowAcceptedSuccessDialog(true);
        setShowAcceptDialog(false);
      } else {
        console.error("Failed to accept mentorship:", response);
        toast.error("Failed to accept mentorship. Please try again.");
      }
    } catch (error) {
      console.error("Error accepting mentorship:", error);
      toast.error("An error occurred while accepting mentorship. Please try again.");
    }
  };

  const handleSecondaryActionConfirm = async (reason) => {
    try {
      if (!id || !user?.id) {
        console.error("Missing idea ID or user ID");
        return;
      }

      const response = await rejectMentorship(id, user.id, reason);

      if (response?.success || response?.status === 200) {
        // Sequential routing: declining hands the idea off to the student's next
        // preferred mentor (or finalizes it if none remain). Either way it leaves
        // this mentor's review queue.
        const awaitingAdmin = response?.data?.data?.awaitingAdmin;
        setShowRejectDialog(false);
        toast.success(
          awaitingAdmin
            ? "You've declined this idea. No further preferred mentors remain, so the IgniteNext team will review it."
            : "You've declined this idea. It has been forwarded to the student's next preferred mentor."
        );
        navigate("/ideas-review");
      } else {
        console.error("Failed to decline idea:", response);
        toast.error("Failed to decline the idea. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting mentorship:", error);
      toast.error("An error occurred while rejecting mentorship. Please try again.");
    }
  };

  const getFileIcon = (type) => {
    const icons = {
      pdf: { bg: "bg-red-500", text: "PDF" },
      jpg: { bg: "bg-purple-500", text: "JPG" },
      jpeg: { bg: "bg-purple-500", text: "JPG" },
      mp4: { bg: "bg-blue-500", text: "MP4" },
      doc: { bg: "bg-blue-500", text: "DOC" },
      docx: { bg: "bg-blue-500", text: "DOC" },
      default: { bg: "bg-gray-500", text: "FILE" },
    };
    return icons[type?.toLowerCase()] || icons.default;
  };

  const formatFileSize = (size) => {
    return size;
  };

  const handleFileUpload = async (filesToUpload, ideaId) => {
    try {
      // TODO: Replace with actual API call when available
      const newFiles = filesToUpload.map((fileData, index) => {
        const file = fileData.file;
        const fileExtension = file.name.split(".").pop().toLowerCase();
        const fileSize = formatFileSizeBytes(file.size);

        return {
          id: Date.now() + index,
          name: file.name,
          size: fileSize,
          type: fileExtension,
          fileTag: fileData.tag || "",
          uploadedOn: new Date().toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          uploadedBy: {
            name: user?.first_name && user?.last_name
              ? `${user.first_name} ${user.last_name}`
              : user?.name || "User",
            email: user?.email || "",
            avatar: user?.profile_picture_url || "",
          },
        };
      });

      // Update idea state with new files
      setIdea((prevIdea) => ({
        ...prevIdea,
        files: [...(prevIdea.files || []), ...newFiles],
      }));

      console.log("Files uploaded:", newFiles);
    } catch (error) {
      console.error("Error uploading files:", error);
      throw new Error("Failed to upload files. Please try again.");
    }
  };

  const formatFileSizeBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex flex-col w-full gap-6 bg-slate-50 px-8 py-8 min-h-screen">
        <div className="animate-pulse flex flex-col gap-6">
          <div className="h-8 w-72 rounded-lg bg-slate-200" />
          <div className="h-28 w-full rounded-2xl bg-slate-200" />
          <div className="h-12 w-full rounded-xl bg-slate-100" />
          <div className="h-80 w-full rounded-2xl bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-slate-50">
        <div className="h-20 w-20 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Icon icon="lucide:file-x" className="h-10 w-10 text-slate-400" />
        </div>
        <div className="text-center">
          <Typography as="h2" className="text-xl font-bold text-slate-900">Idea not found</Typography>
          <Typography as="p" className="text-sm text-slate-500 mt-1">This idea may have been removed or you may not have access.</Typography>
        </div>
        <Button onClick={() => navigate(-1)} className="rounded-xl">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary min-h-screen px-6 py-6 lg:px-10 lg:py-8">
      {/* Header Section */}
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-start sm:justify-between flex-1 w-full">
          <Typography as="h1" className="text-2xl font-bold text-primary-900 tracking-tight">
            {idea.title}
          </Typography>

          {/* Action Buttons - Mentor specific (hidden in read-only/admin mode) */}
          {roleActions && !isReadOnly && (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button
                variant="secondary"
                size="medium"
                startIcon={<Icon icon="lucide:message-circle-question" className="h-5 w-5" />}
                onClick={() => setShowClarityDialog(true)}
                className="border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
              >
                Request Clarity
              </Button>
              {roleActions.primaryAction && (
                <Button
                  variant="primary"
                  size="medium"
                  startIcon={<Icon icon={roleActions.primaryAction.icon} className="h-5 w-5" />}
                  onClick={handlePrimaryAction}
                  className={roleActions.primaryAction.className}
                >
                  {roleActions.primaryAction.label}
                </Button>
              )}
              {roleActions.secondaryAction && (
                <Button
                  variant="primary"
                  size="medium"
                  startIcon={<Icon icon={roleActions.secondaryAction.icon} className="h-5 w-5" />}
                  onClick={handleSecondaryAction}
                  className={roleActions.secondaryAction.className}
                >
                  {roleActions.secondaryAction.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin read-only banner */}
      {isReadOnly && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Icon icon="lucide:eye" className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <Typography as="p" className="text-sm font-semibold text-amber-800">Admin Read-Only View</Typography>
            <Typography as="p" className="text-xs text-amber-600">You are viewing this idea as an administrator. No changes can be made.</Typography>
          </div>
        </div>
      )}

      {/* Progress Timeline — visible in admin read-only mode to show idea stage */}
      {isReadOnly && (
        <ProgressTimeline
          stages={idea?.stages || []}
          currentStage={activeStage}
          daysRemaining={idea?.daysRemaining || null}
          onStageClick={() => {}}
        />
      )}

      {/* Stage Content - Shows tabs content when Spark stage is active, or always for read-only/admin */}
      {(activeStage === 0 || isReadOnly) && (
        <div className="w-full">
          {/* Tabs Navigation */}
          <Tabs
            variant="segment"
            tabs={[
              { id: "overview", label: "Idea Overview" },
              { id: "attachments", label: "Attachments" },
              { id: "questionnaire", label: "Applicant Questionnaire" },
              { id: "collaboration", label: "Collaboration" },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="py-6 min-h-[400px] text-left">
            {activeTab === "overview" && (
              <div className="flex flex-col gap-6">
                {/* Top Section - Meta Information */}
                <div className="flex items-center justify-between w-full flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-violet-600" />
                    <Typography
                      as="p"
                      className="text-sm font-medium text-violet-600"
                    >
                      Submitted: {idea.submissionDate}
                    </Typography>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-sm font-medium text-violet-600 hover:bg-violet-100 transition-colors"
                    >
                      <Icon icon="lucide:info" className="h-4 w-4" />
                      Rubrics at this stage
                    </button>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
                      {(() => {
                        // Use primaryStudent from navigation state or idea object, fallback to submitter
                        const student = idea.primaryStudent || null;
                        const studentAvatar = student?.profile_picture_url || idea.submitter.avatar;
                        const studentName = student 
                          ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
                          : idea.submitter.name;
                        const studentEmail = student?.email || idea.submitter.email;
                        
                        return (
                          <>
                            <Avatar
                              src={
                                studentAvatar &&
                                  studentAvatar.length >= 10 &&
                                  (studentAvatar.startsWith("http") ||
                                    studentAvatar.startsWith("data:image") ||
                                    studentAvatar.startsWith("/"))
                                  ? studentAvatar
                                  : null
                              }
                              firstName={student?.first_name || idea.submitter.name?.split(" ")[0]}
                              lastName={student?.last_name || idea.submitter.name?.split(" ").slice(1).join(" ")}
                              email={studentEmail}
                              alt={studentName}
                              size="w-6 h-6"
                            />
                            <Typography
                              as="span"
                              className="text-sm text-slate-700 font-medium"
                            >
                              {studentName}
                            </Typography>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Content Sections */}
                <div className="flex flex-col gap-6 text-start">
                  {/* Problem Statement */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h4"
                      className="text-base font-semibold text-primary-900"
                    >
                      Problem Statement
                    </Typography>
                    <Typography
                      as="p"
                      className="text-sm text-tertiary-600 leading-relaxed"
                    >
                      {idea.problemStatement || idea.description}
                    </Typography>
                  </div>

                  {/* Proposed Solution */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h4"
                      className="text-base font-semibold text-primary-900"
                    >
                      Proposed Solution
                    </Typography>
                    <Typography
                      as="p"
                      className="text-sm text-tertiary-600 leading-relaxed"
                    >
                      {idea.proposedSolution || idea.description}
                    </Typography>
                  </div>
                </div>

                {/* Stage 1 (Shape) fields */}
                {(idea.idealCustomerProfile || idea.valueProposition || idea.productMarketFit || idea.targetAddressableMarket || idea.mvp) && (
                  <div className="flex flex-col gap-5 mt-6 pt-6 border-t border-slate-100 text-start">
                    <Typography as="h4" className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-1">Shape Stage</Typography>
                    {[
                      { label: "Ideal Customer Profile", value: idea.idealCustomerProfile, url: idea.idealCustomerProfileUrl },
                      { label: "Value Proposition", value: idea.valueProposition, url: idea.valuePropositionUrl },
                      { label: "Product Market Fit", value: idea.productMarketFit, url: idea.productMarketFitUrl },
                      { label: "Target Addressable Market", value: idea.targetAddressableMarket, url: idea.targetAddressableMarketUrl },
                      { label: "MVP", value: idea.mvp, url: idea.mvpUrl },
                    ].filter(f => f.value).map(({ label, value, url }) => (
                      <div key={label} className="flex flex-col gap-1.5">
                        <Typography as="h5" className="text-base font-semibold text-primary-900">{label}</Typography>
                        <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">{value}</Typography>
                      </div>
                    ))}
                  </div>
                )}

                {/* Stage 2 (Secure) fields */}
                {(idea.marketValidation || idea.financialProjections || idea.microGrantProposal || idea.enhanceBusinessModel) && (
                  <div className="flex flex-col gap-5 mt-6 pt-6 border-t border-slate-100 text-start">
                    <Typography as="h4" className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-1">Structure Stage</Typography>
                    {[
                      { label: "Market Validation", value: idea.marketValidation, url: idea.marketValidationUrl },
                      { label: "Financial Projections", value: idea.financialProjections, url: idea.financialProjectionsUrl },
                      { label: "Micro Grant Proposal", value: idea.microGrantProposal, url: idea.microGrantProposalUrl },
                      { label: "Enhance Business Model", value: idea.enhanceBusinessModel, url: idea.enhanceBusinessModelUrl },
                    ].filter(f => f.value).map(({ label, value, url }) => (
                      <div key={label} className="flex flex-col gap-1.5">
                        <Typography as="h5" className="text-base font-semibold text-primary-900">{label}</Typography>
                        <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">{value}</Typography>
                      </div>
                    ))}
                  </div>
                )}

                {/* Stage 3 (Scale) fields */}
                {(idea.productReadiness || idea.customerProfile || idea.gtmStrategy || idea.customerFeedback) && (
                  <div className="flex flex-col gap-5 mt-6 pt-6 border-t border-slate-100 text-start">
                    <Typography as="h4" className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-1">Scale Stage</Typography>
                    {[
                      { label: "Product Readiness", value: idea.productReadiness, url: idea.productReadinessUrl },
                      { label: "Customer Profile", value: idea.customerProfile, url: idea.customerProfileUrl },
                      { label: "GTM Strategy", value: idea.gtmStrategy, url: idea.gtmStrategyUrl },
                      { label: "Customer Feedback", value: idea.customerFeedback, url: idea.customerFeedbackUrl },
                    ].filter(f => f.value).map(({ label, value, url }) => (
                      <div key={label} className="flex flex-col gap-1.5">
                        <Typography as="h5" className="text-base font-semibold text-primary-900">{label}</Typography>
                        <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">{value}</Typography>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}

            {activeTab === "attachments" && (
              <IdeaAttachmentsSection engagement={idea} />
            )}

            {activeTab === "collaboration" && (
              <div className="flex flex-col gap-6">
                {/* Team Members Card */}
                <div className="grid grid-cols-1 gap-6">
                  {/* Team Members Card */}
                  <Card className="w-full">
                    <div className="flex flex-col gap-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                          <Typography
                            as="h3"
                            className="text-lg font-semibold text-primary-900"
                          >
                            Team Members
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            View the team members working on this idea
                          </Typography>
                        </div>
                      </div>

                      {/* Table */}
                      <Table
                        columns={[
                          {
                            header: "Member Name",
                            accessor: "name",
                            render: (value, record) => (
                              <div className="flex items-center gap-3">
                                <Avatar
                                  src={
                                    record.avatar &&
                                      record.avatar.length >= 10 &&
                                      (record.avatar.startsWith("http") ||
                                        record.avatar.startsWith("data:image") ||
                                        record.avatar.startsWith("/"))
                                      ? record.avatar
                                      : null
                                  }
                                  firstName={record.name?.split(" ")[0]}
                                  lastName={record.name?.split(" ").slice(1).join(" ")}
                                  email={record.email}
                                  alt={record.name}
                                  size="w-10 h-10"
                                />
                                <div className="flex flex-col">
                                  <Typography
                                    as="p"
                                    className="text-sm text-primary-900 font-medium"
                                  >
                                    {record.name}
                                  </Typography>
                                  <Typography
                                    as="p"
                                    className="text-xs text-tertiary-500"
                                  >
                                    {record.email}
                                  </Typography>
                                </div>
                              </div>
                            ),
                          },
                          {
                            header: "Member Role",
                            accessor: "role",
                            render: (value) => (
                              <Typography
                                as="p"
                                className="text-sm text-tertiary-600"
                              >
                                {value}
                              </Typography>
                            ),
                          },
                        ]}
                        data={ideaTeamRows
                          .filter(
                            (row) =>
                              row?.role === "PRIMARY_STUDENT" ||
                              row?.role === "SECONDARY_STUDENT"
                          )
                          .map((row) => ({
                            id: row.id,
                            name:
                              `${row.user?.first_name || ""} ${row.user?.last_name || ""}`.trim() ||
                              row.user?.email ||
                              "Unknown",
                            email: row.user?.email || "",
                            avatar: row.user?.profile_picture_url || "",
                            role:
                              row.role === "PRIMARY_STUDENT"
                                ? "Primary (Owner)"
                                : "Team Member",
                          }))}
                        rowKey="id"
                        emptyMessage="No team members added yet."
                        className="w-full"
                      />
                    </div>
                  </Card>

                </div>
              </div>
            )}

            {activeTab === "questionnaire" && (
              <div className="flex flex-col gap-6">
                {/* Header */}
                <div className="flex flex-col gap-1">
                  <Typography
                    as="h3"
                    className="text-lg font-semibold text-primary-900"
                  >
                    Applicant Questionnaire
                  </Typography>
                  <Typography
                    as="p"
                    className="text-sm text-tertiary-600"
                  >
                    Review the applicant's responses to the questionnaire.
                  </Typography>
                </div>

                {/* Default Questions */}
                <div className="flex flex-col gap-4">
                  {/* Question 1: Why does innovation excite you? */}
                  <Card className="w-full">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <Typography
                            as="span"
                            className="text-sm font-semibold text-purple-600"
                          >
                            1
                          </Typography>
                        </div>
                        <div className="flex-1 flex flex-col gap-2">
                          <Typography
                            as="h4"
                            className="text-base font-semibold text-primary-900"
                          >
                            Why does innovation excite you?
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap"
                          >
                            {idea.innovationExcitesYou ||
                              sanitizeStudentResponse(
                                idea.questionnaireResponses?.find((r) =>
                                  r.question?.toLowerCase().includes("innovation")
                                )?.answer
                              ) ||
                              "No response provided."}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Question 2: How do you define success? */}
                  <Card className="w-full">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <Typography
                            as="span"
                            className="text-sm font-semibold text-purple-600"
                          >
                            2
                          </Typography>
                        </div>
                        <div className="flex-1 flex flex-col gap-2">
                          <Typography
                            as="h4"
                            className="text-base font-semibold text-primary-900"
                          >
                            How do you define success?
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap"
                          >
                            {idea.successDefinition ||
                              sanitizeStudentResponse(
                                idea.questionnaireResponses?.find((r) =>
                                  r.question?.toLowerCase().includes("success")
                                )?.answer
                              ) ||
                              "No response provided."}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Question 3: Achievement Summary */}
                  <Card className="w-full">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <Typography
                            as="span"
                            className="text-sm font-semibold text-purple-600"
                          >
                            3
                          </Typography>
                        </div>
                        <div className="flex-1 flex flex-col gap-2">
                          <Typography
                            as="h4"
                            className="text-base font-semibold text-primary-900"
                          >
                            Achievement Summary
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap"
                          >
                            {idea.achievementSummary ||
                              sanitizeStudentResponse(
                                idea.questionnaireResponses?.find((r) =>
                                  r.question?.toLowerCase().includes("achievement")
                                )?.answer
                              ) ||
                              sanitizeStudentResponse(idea.primaryStudent?.achievement) ||
                              "No response provided."}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Additional Questionnaire Responses from API */}
                  {idea.questionnaireResponses && idea.questionnaireResponses.length > 0 && (
                    <>
                      {idea.questionnaireResponses
                        .filter(response => {
                          const questionLower = response.question?.toLowerCase() || "";
                          return !questionLower.includes("innovation") && !questionLower.includes("success") && !questionLower.includes("achievement");
                        })
                        .map((response, index) => (
                          <Card key={response.id || `additional-${index}`} className="w-full">
                            <div className="flex flex-col gap-3">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                                  <Typography
                                    as="span"
                                    className="text-sm font-semibold text-purple-600"
                                  >
                                    {index + 4}
                                  </Typography>
                                </div>
                                <div className="flex-1 flex flex-col gap-2">
                                  <Typography
                                    as="h4"
                                    className="text-base font-semibold text-primary-900"
                                  >
                                    {response.question || `Question ${index + 3}`}
                                  </Typography>
                                  <Typography
                                    as="p"
                                    className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap"
                                  >
                                    {response.answer || "No answer provided."}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <IdeaClarityDialog
        isOpen={showClarityDialog}
        onClose={() => setShowClarityDialog(false)}
        onSubmit={handleSaveIdeaClarity}
        recipientEmail={idea?.submitter?.email || ""}
        recipientName={idea?.submitter?.name || ""}
        ideaTitle={idea?.title || ""}
        initialBody={idea?.ideaClarity || ""}
        defaultCc={user?.email || ""}
        saving={savingClarity}
      />

      {/* Dialogs - Mentor specific */}
      {roleActions?.primaryAction && (
        <AcceptMentorshipDialog
          isOpen={showAcceptDialog}
          onClose={() => setShowAcceptDialog(false)}
          onAccept={handlePrimaryActionConfirm}
          title={roleActions.primaryAction.dialogTitle}
          description={roleActions.primaryAction.dialogDescription}
          buttonLabel={roleActions.primaryAction.label}
        />
      )}
      {roleActions?.secondaryAction && (
        <RejectMentorshipDialog
          isOpen={showRejectDialog}
          onClose={() => setShowRejectDialog(false)}
          onReject={handleSecondaryActionConfirm}
          title={roleActions.secondaryAction.dialogTitle}
          description={roleActions.secondaryAction.dialogDescription}
          buttonLabel={roleActions.secondaryAction.label}
        />
      )}
      {roleActions?.primaryAction && (
        <IdeaAcceptedDialog
          isOpen={showAcceptedSuccessDialog}
          onClose={() => setShowAcceptedSuccessDialog(false)}
          title={roleActions.primaryAction.successMessage}
          description="Your action has been successfully recorded. You can view this in your engagements."
          showViewButton={true}
        />
      )}

      {/* Upload File Dialog */}
      <UploadFileDialog
        isOpen={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onUpload={handleFileUpload}
        ideaId={id}
      />

      {/* Back Button - Bottom */}
      <div className="flex justify-start pt-2">
        <Button
          variant="secondary"
          size="medium"
          startIcon={<Icon icon="lucide:arrow-left" className="h-5 w-5" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </div>
    </div>
  );
};

export default MentorIdeaDetail;

